import React, { createContext } from 'react';
import { initializeApp, getApp, getApps } from 'firebase/app';
import { getDatabase, ref, query, orderByChild, equalTo, limitToLast, startAt, endAt } from 'firebase/database';
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  RecaptchaVerifier,
  unlink,
  updatePhoneNumber,
  linkWithPhoneNumber,
} from 'firebase/auth';
import { getStorage, ref as stRef } from 'firebase/storage';
import { getAnalytics, isSupported as analyticsSupported } from 'firebase/analytics';
import { initializeAppCheck, ReCaptchaEnterpriseProvider, getToken as getAppCheckTokenSdk } from 'firebase/app-check';

// Clave pública de reCAPTCHA Enterprise registrada en App Check para la app web (panel).
const APP_CHECK_RECAPTCHA_SITE_KEY = '6LeRyAEuAAAAADvGLInIgpOh7-rHXpXkcKsDsxCb';
const APP_CHECK_TOKEN_TIMEOUT_MS = 3000;

let appCheck = null;
const initWebAppCheck = (app) => {
  if (appCheck || typeof window === 'undefined') return appCheck;
  try {
    appCheck = initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(APP_CHECK_RECAPTCHA_SITE_KEY),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (e) {
    appCheck = null;
  }
  return appCheck;
};

/** Token para llamadas a Functions; null si no hay App Check o tarda (nunca bloquea la llamada). */
const getAppCheckToken = () => {
  if (!appCheck) return Promise.resolve(null);
  return Promise.race([
    getAppCheckTokenSdk(appCheck, false).then((r) => (r && r.token) || null).catch(() => null),
    new Promise((resolve) => setTimeout(() => resolve(null), APP_CHECK_TOKEN_TIMEOUT_MS)),
  ]);
};

const FirebaseContext = createContext(null);

let firebase = {
  app: null,
  database: null,
  auth: null,
  storage: null,
};

const createFullStructure = (app, db, auth, storage, config) => ({
  app,
  config,
  appcat: config?.appcat || 'bidcab',
  database: db,
  auth,
  storage,
  authRef: getAuth,
  googleProvider: new GoogleAuthProvider(),
  appleProvider: new OAuthProvider('apple.com'),
  phoneProvider: new PhoneAuthProvider(auth),
  RecaptchaVerifier,
  signInWithPhoneNumber,
  updatePhoneNumber,
  unlink,
  googleCredential: (idToken, accessToken) => GoogleAuthProvider.credential(idToken, accessToken),
  linkWithPhoneNumber,
  mobileAuthCredential: (verificationId, code) => PhoneAuthProvider.credential(verificationId, code),
  usersRef: ref(db, 'users'),
  bookingRef: ref(db, 'bookings'),
  cancelreasonRef: ref(db, 'cancel_reason'),
  settingsRef: ref(db, 'settings'),
  smtpRef: ref(db, 'smtpdata'),
  smsRef: ref(db, 'smsConfig'),
  carTypesRef: ref(db, 'cartypes'),
  carTypesEditRef: (id) => ref(db, 'cartypes/' + id),
  carDocImage: (id) => stRef(storage, `cartypes/${id}`),
  zonificacionCitiesRef: ref(db, 'zonificacion/colombia/cities'),
  zonificacionCityEditRef: (id) => ref(db, 'zonificacion/colombia/cities/' + id),
  zonificacionCodesRef: ref(db, 'zonificacion/colombia/codes'),
  zonificacionCodeRef: (code) => ref(db, 'zonificacion/colombia/codes/' + code),
  zonificacionCityCodesRef: (cityId) => ref(db, 'zonificacion/colombia/cityCodes/' + cityId),
  promoRef: ref(db, 'promos'),
  promoEditRef: (id) => ref(db, 'promos/' + id),
  notifyRef: ref(db, 'notifications/'),
  notifyEditRef: (id) => ref(db, 'notifications/' + id),
  addressRef: (uid, id) => ref(db, 'savedAddresses/' + uid + '/' + id),
  addressEditRef: (uid) => ref(db, 'savedAddresses/' + uid),
  singleUserRef: (uid) => ref(db, 'users/' + uid),
  profileImageRef: (uid) => stRef(storage, `users/${uid}/profileImage`),
  verifyIdImageRef: (uid) => stRef(storage, `users/${uid}/verifyIdImage`),
  bookingImageRef: (bookingId, imageType) => stRef(storage, `bookings/${bookingId}/${imageType}`),
  driverPublicRef: (uid) => ref(db, 'drivers_public/' + uid),
  activeDriversPublicRef: query(ref(db, 'drivers_public'), orderByChild('active'), equalTo(true)),
  driverDocsRef: (uid) => stRef(storage, `users/${uid}/license`),
  driverDocsRefBack: (uid) => stRef(storage, `users/${uid}/licenseBack`),
  singleBookingRef: (bookingKey) => ref(db, 'bookings/' + bookingKey),
  requestedDriversRef: (bookingKey) => ref(db, 'bookings/' + bookingKey + '/requestedDrivers'),
  trackingRef: (bookingId) => ref(db, 'tracking/' + bookingId),
  bookingRouteRef: (bookingId) => ref(db, 'booking_routes/' + bookingId),
  tasksRef: (limit) => {
    const n = Number(limit) > 0 ? Number(limit) : 60;
    return query(ref(db, 'bookings'), orderByChild('status'), equalTo('NEW'), limitToLast(n));
  },
  singleTaskRef: (uid, bookingId) => ref(db, 'bookings/' + bookingId + '/requestedDrivers/' + uid),
  driverRequestsRef: (uid) => ref(db, 'driver_requests/' + uid),
  bookingListRef: (uid, role, limit) => {
    const roleField =
      role == 'customer' ? 'customer'
        : role == 'driver' ? 'driver'
          : role == 'fleetadmin' ? 'fleetadmin'
            : null;
    if (!roleField) {
      return limit && Number(limit) > 0
        ? query(ref(db, 'bookings'), limitToLast(Number(limit)))
        : ref(db, 'bookings');
    }
    if (limit && Number(limit) > 0) {
      return query(
        ref(db, 'bookings'),
        orderByChild(roleField),
        equalTo(uid),
        limitToLast(Number(limit))
      );
    }
    return query(ref(db, 'bookings'), orderByChild(roleField), equalTo(uid));
  },
  chatRef: (bookingId) => ref(db, 'chats/' + bookingId + '/messages'),
  withdrawRef: ref(db, 'withdraws/'),
  languagesRef: ref(db, 'languages'),
  languagesEditRef: (id) => ref(db, 'languages/' + id),
  langEditRef: (id) => ref(db, `languages/${id}/keyValuePairs/`),
  walletHistoryRef: (uid) => ref(db, 'walletHistory/' + uid),
  userNotificationsRef: (uid) => ref(db, 'userNotifications/' + uid),
  userRatingsRef: (uid) => ref(db, 'userRatings/' + uid),
  carsRef: (uid, role) =>
    role == 'driver'
      ? query(ref(db, 'cars'), orderByChild('driver'), equalTo(uid))
      : role == 'fleetadmin'
        ? query(ref(db, 'cars'), orderByChild('fleetadmin'), equalTo(uid))
        : ref(db, 'cars'),
  carAddRef: ref(db, 'cars'),
  carEditRef: (id) => ref(db, 'cars/' + id),
  carImage: (id) => stRef(storage, `cars/${id}`),
  carRegistrationImage: (id) => stRef(storage, `cars/${id}_registration`),
  allLocationsRef: ref(db, 'locations'),
  userLocationRef: (uid) => ref(db, 'locations/' + uid),
  locationsByGeohashRef: (prefix) => query(ref(db, 'locations'), orderByChild('geohash'), startAt(prefix), endAt(prefix + '\uf8ff')),
  sosRef: ref(db, 'sos'),
  editSosRef: (id) => ref(db, 'sos/' + id),
  complainRef: ref(db, 'complain'),
  leaderAlertsRef: ref(db, 'leaderAlerts'),
  leaderAlertEditRef: (id) => ref(db, 'leaderAlerts/' + id),
  leaderWeeklyHistoryRef: ref(db, 'leaderWeeklyHistory'),
  leaderWeeklyHistoryUserRef: (uid) => ref(db, 'leaderWeeklyHistory/' + uid),
  leaderChangesRef: ref(db, 'leaderChanges'),
  fareRecommendationsRef: ref(db, 'fare_recommendations'),
  fareRecommendationEditRef: (cityId) => ref(db, `fare_recommendations/${cityId}`),
  fareMarketBenchmarksRef: ref(db, 'fare_market_benchmarks'),
  fareMarketBenchmarkCityRef: (cityKey) => ref(db, `fare_market_benchmarks/${cityKey}`),
  fareMarketBenchmarkCarRef: (cityKey, carKey) =>
    ref(db, `fare_market_benchmarks/${cityKey}/${carKey}`),
  adminSecretsRef: ref(db, 'admin_secrets'),
  geminiApiKeyRef: ref(db, 'admin_secrets/geminiApiKey'),
  deepseekApiKeyRef: ref(db, 'admin_secrets/deepseekApiKey'),
  walletAdminCreditsRef: ref(db, 'walletAdminCredits'),
  walletCampaignsRef: ref(db, 'walletCampaigns'),
  walletCampaignRef: (id) => ref(db, 'walletCampaigns/' + id),
  walletCampaignGrantsRef: (id) => ref(db, 'walletCampaignGrants/' + id),
  editComplainRef: (id) => ref(db, 'complain/' + id),
  paymentSettingsRef: ref(db, 'payment_settings'),
  usedreferralRef: ref(db, 'usedreferral'),
  withdrawComRef: ref(db, 'withdrawsComi/'),
  referralBonusRef: (id) => ref(db, 'bonusReferral/' + id),
  Total_raceRef: (uid) => query(ref(db, 'bookings'), orderByChild('driver'), equalTo(uid)),
});

function createAuth(app) {
  // Web: getAuth registra Auth sobre la misma instancia de app (evita
  // "Component auth has not been registered yet" por initializeAuth/duplicados).
  // RN usa configureFirebase.native.js.
  return getAuth(app);
}

function ensureFirebase(config, AsyncStorage) {
  if (firebase.app && firebase.auth && firebase.database && firebase.storage) {
    return firebase;
  }

  let app;
  let auth;
  let database;
  let storage;

  if (!getApps().length) {
    app = initializeApp(config);
    // Antes de usar Database/Storage/Auth para que sus peticiones ya lleven el token.
    initWebAppCheck(app);
    auth = createAuth(app);
    database = getDatabase(app);
    storage = getStorage(app);
    if (config.measurementId) {
      analyticsSupported().then((ok) => { if (ok) getAnalytics(app); }).catch(() => {});
    }
  } else {
    app = getApp();
    initWebAppCheck(app);
    try {
      auth = getAuth(app);
    } catch (_) {
      auth = createAuth(app);
    }
    database = getDatabase(app);
    storage = getStorage(app);
  }

  firebase = createFullStructure(app, database, auth, storage, config);
  firebase.getAppCheckToken = getAppCheckToken;
  return firebase;
}

const FirebaseProvider = ({ config, children, AsyncStorage }) => {
  // No hooks aquí: evita "Cannot read properties of null (reading 'useMemo')"
  // cuando el monorepo resuelve más de una copia de React en el bundle web.
  const value = ensureFirebase(config, AsyncStorage);
  return React.createElement(FirebaseContext.Provider, { value }, children);
};

export { firebase, FirebaseProvider, FirebaseContext };
